const DOW_NAMES: Record<string, string> = {
  SUN: 'Sundays',
  MON: 'Mondays',
  TUE: 'Tuesdays',
  WED: 'Wednesdays',
  THU: 'Thursdays',
  FRI: 'Fridays',
  SAT: 'Saturdays',
  '0': 'Sundays',
  '1': 'Mondays',
  '2': 'Tuesdays',
  '3': 'Wednesdays',
  '4': 'Thursdays',
  '5': 'Fridays',
  '6': 'Saturdays',
  '7': 'Sundays',
}

const DOW_INDEX: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
  '0': 0,
  '1': 1,
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
  '7': 0,
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function clockLabel(hour: number, minute: number): string {
  const suffix = hour >= 12 ? 'pm' : 'am'
  const h12 = hour % 12 === 0 ? 12 : hour % 12
  if (minute === 0) return `${h12}:00${suffix}`
  return `${h12}:${String(minute).padStart(2, '0')}${suffix}`
}

function zoneLabel(timezone: string): string {
  return timezone === 'America/Los_Angeles' ? 'PT' : timezone
}

export type CronParts = {
  minute: number
  hour: number
  dom: string
  month: string
  dow: string
}

export function parseSimpleCron(expr: string): CronParts | null {
  const parts = expr.trim().split(/\s+/)
  if (parts.length < 5) return null
  const [minute, hour, dom, month, dow] = parts
  if (!/^\d+$/.test(minute) || !/^\d+$/.test(hour)) return null
  return {
    minute: Number(minute),
    hour: Number(hour),
    dom,
    month,
    dow: dow.toUpperCase(),
  }
}

/** Human label for a 5-field cron. Null when the shape is not one we name. */
export function formatCronExpr(expr: string, timezone = 'America/Los_Angeles'): string | null {
  const parsed = parseSimpleCron(expr)
  if (!parsed) return null
  const clock = clockLabel(parsed.hour, parsed.minute)
  const zone = zoneLabel(timezone)
  if (parsed.dom === '*' && parsed.month === '*' && parsed.dow === 'MON-FRI') {
    return `Weekdays at ${clock} ${zone}`
  }
  if (parsed.dom === '*' && parsed.month === '*' && parsed.dow === '*') {
    return `Daily at ${clock} ${zone}`
  }
  const named = DOW_NAMES[parsed.dow]
  if (parsed.dom === '*' && parsed.month === '*' && named) {
    return `${named} at ${clock} ${zone}`
  }
  const monthIndex = Number(parsed.month)
  const day = Number(parsed.dom)
  if (
    parsed.dow === '*' &&
    Number.isInteger(monthIndex) &&
    monthIndex >= 1 &&
    monthIndex <= 12 &&
    Number.isInteger(day)
  ) {
    return `${MONTHS[monthIndex - 1]} ${day} at ${clock} ${zone}`
  }
  return null
}

function zonedWeekday(date: Date, timeZone: string): number | null {
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
  }).format(date)
  return WEEKDAY_INDEX[weekday] ?? null
}

function zonedDateLabel(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date)
}

function zonedHourMinute(date: Date, timeZone: string): { hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const hour = Number(parts.find((p) => p.type === 'hour')?.value)
  const minute = Number(parts.find((p) => p.type === 'minute')?.value)
  return { hour, minute }
}

/** Next calendar label for a single weekday cron, or null. */
export function nextWeeklyCronLabel(
  expr: string,
  timezone = 'America/Los_Angeles',
  from = new Date(),
): string | null {
  const parsed = parseSimpleCron(expr)
  if (!parsed || parsed.dom !== '*' || parsed.month !== '*') return null
  const target = DOW_INDEX[parsed.dow]
  if (target == null) return null
  for (let add = 0; add < 8; add++) {
    const day = new Date(from.getTime() + add * 24 * 60 * 60 * 1000)
    if (zonedWeekday(day, timezone) !== target) continue
    if (add === 0) {
      const now = zonedHourMinute(from, timezone)
      if (now.hour > parsed.hour || (now.hour === parsed.hour && now.minute > parsed.minute)) {
        continue
      }
    }
    return zonedDateLabel(day, timezone)
  }
  return null
}

/** What to show in the Next column: a date when we can name one, otherwise the schedule. */
export function formatNextCron(
  expr: string,
  timezone = 'America/Los_Angeles',
  from = new Date(),
): string {
  const weekly = nextWeeklyCronLabel(expr, timezone, from)
  if (weekly) return weekly
  const parsed = parseSimpleCron(expr)
  if (parsed && parsed.dom === '*' && parsed.month === '*' && parsed.dow === '*') {
    const now = zonedHourMinute(from, timezone)
    const passed = now.hour > parsed.hour || (now.hour === parsed.hour && now.minute > parsed.minute)
    return passed ? 'Tomorrow' : 'Today'
  }
  return formatCronExpr(expr, timezone) ?? `Cron: ${expr}`
}
